import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { apiFetch } from '../lib/api'

const statusBadges = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendiente' },
  in_production: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'En Produccion' },
  ready: { bg: 'bg-green-100', text: 'text-green-800', label: 'Lista' }
}

export default function ClinicDashboard() {
  const [patients, setPatients] = useState([])
  const [requests, setRequests] = useState([])
  const [activeTab, setActiveTab] = useState('patients')
  const [searchTerm, setSearchTerm] = useState('')
  const [requestSearchTerm, setRequestSearchTerm] = useState('')
  const [requestStatusFilter, setRequestStatusFilter] = useState('all')
  const [showNewPatientModal, setShowNewPatientModal] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const navigate = useNavigate()
  const { currentUser, logout } = useAuth()

  useEffect(() => {
    const load = async () => {
      try {
        const patientsUrl = (currentUser?.role === 'clinic' && currentUser?.clinicId)
          ? `/api/patients?clinic_id=${currentUser.clinicId}`
          : '/api/patients'
        const res = await apiFetch(patientsUrl)
        setPatients(res.ok ? await res.json() : [])

        const requestsUrl = (currentUser?.role === 'clinic' && currentUser?.clinicId)
          ? `/api/requests?clinic_id=${currentUser.clinicId}`
          : '/api/requests'
        const reqRes = await apiFetch(requestsUrl)
        setRequests(reqRes.ok ? await reqRes.json() : [])
      } catch (err) {
        console.error('Error loading data:', err)
        setPatients([])
        setRequests([])
      }
    }

    load()
    const interval = setInterval(load, 30000)
    return () => clearInterval(interval)
  }, [currentUser?.clinicId, currentUser?.role])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleBackToAdmin = () => {
    if (currentUser?.role === 'admin') navigate('/admin')
  }

  const filteredPatients = patients.filter((p) => {
    const term = searchTerm.toLowerCase()
    return (
      (p.name || '').toLowerCase().includes(term) ||
      (p.email || '').toLowerCase().includes(term) ||
      (p.phone || '').includes(searchTerm)
    )
  })

  const groupedPatients = Object.entries(
    filteredPatients.reduce((acc, p) => {
      const letter = (p.name?.trim()?.charAt(0) || '#').toUpperCase()
      const key = /[A-Z]/i.test(letter) ? letter : '#'
      acc[key] = acc[key] || []
      acc[key].push(p)
      return acc
    }, {})
  ).sort((a, b) => a[0].localeCompare(b[0]))

  const activeRequests = requests.filter((r) => r.status !== 'cancelled' && r.status !== 'delivered')
  const filteredRequests = activeRequests
    .filter((r) => requestStatusFilter === 'all' || r.status === requestStatusFilter)
    .filter((req) => {
      const patient = patients.find((p) => p.id === req.patient_id)
      return (patient?.name || '').toLowerCase().includes(requestSearchTerm.toLowerCase())
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

  const handleCreatePatient = async (e) => {
    e.preventDefault()
    const form = e.currentTarget
    const firstName = form.elements.firstName.value.trim()
    const lastName1 = form.elements.lastName1.value.trim()
    const lastName2 = form.elements.lastName2.value.trim()
    const notes = form.elements.notes.value.trim() || null

    if (!firstName || !lastName1 || !lastName2) return
    if (!currentUser?.clinicId) {
      alert('Tu usuario no tiene una clinica asignada. Pide al administrador que te asigne una clinica.')
      return
    }

    const fullName = `${firstName} ${lastName1} ${lastName2}`.trim()

    try {
      const res = await apiFetch('/api/patients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: fullName,
          clinic_id: currentUser.clinicId,
          notes
        })
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Error al guardar paciente')
      setPatients((prev) => [data, ...prev])
      setShowNewPatientModal(false)
      form.reset()
    } catch (err) {
      alert(err.message)
    }
  }

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex flex-col">
      {/* Mobile Top Bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white shadow flex items-center justify-between px-4 h-14">
        <button onClick={() => navigate('/')} className="flex items-center gap-2">
          <img src="/lucvan-logo-web.png" alt="Logo" className="h-9" />
        </button>
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg border border-gray-200 shadow-sm bg-white"
          aria-label="Abrir menu"
        >
          <span className="text-xl">☰</span>
        </button>
      </div>

      {/* Sidebar Desktop */}
      <aside className="hidden md:flex w-64 bg-gradient-to-b from-[#2c3e50] to-[#34495e] text-white flex-col shadow-2xl fixed h-screen">
        {/* Logo */}
        <button onClick={() => navigate('/')} className="p-4 border-b border-white/10 flex justify-center hover:opacity-80 transition-opacity cursor-pointer">
          <img src="/lucvan-logo-web.png" alt="Logo" className="h-16 brightness-0 invert" />
        </button>

        {/* Navegacion */}
        <nav className="flex-1 px-3 py-6 space-y-2">
          <button
            onClick={() => setActiveTab('patients')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === 'patients'
                ? 'bg-[#0066A4] text-white shadow-lg'
                : 'text-gray-300 hover:bg-white/10 hover:text-white'
            }`}
          >
            <span>Pacientes</span>
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === 'requests'
                ? 'bg-[#0066A4] text-white shadow-lg'
                : 'text-gray-300 hover:bg-white/10 hover:text-white'
            }`}
          >
            <span>Solicitudes</span>
          </button>
          {currentUser?.role === 'admin' && (
            <button
              onClick={handleBackToAdmin}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-gray-300 hover:bg-white/10 hover:text-white transition-all"
            >
              <span>Volver a Admin</span>
            </button>
          )}
        </nav>

        {/* Info de usuario */}
        <div className="p-4 border-t border-white/10">
          <div className="mb-3">
            <p className="text-sm font-semibold truncate">{currentUser?.email}</p>
            <p className="text-xs text-gray-400 capitalize">{currentUser?.role === 'clinic' ? 'Clinica' : 'Administrador'}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded font-medium transition-all"
          >
            <span>Cerrar Sesion</span>
          </button>
        </div>
      </aside>

      {/* Sidebar Mobile Drawer */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex pt-14">
          <aside className="w-64 bg-gradient-to-b from-[#2c3e50] to-[#34495e] text-white flex flex-col shadow-2xl h-full">
            {/* Logo y Close Button */}
            <div className="p-4 border-b border-white/10 flex justify-between items-center">
              <img src="/lucvan-logo-web.png" alt="Logo" className="h-12 brightness-0 invert" />
              <button
                onClick={() => setSidebarOpen(false)}
                className="text-2xl hover:text-gray-300"
                aria-label="Cerrar menu"
              >
                ✕
              </button>
            </div>

            {/* Navegacion */}
            <nav className="flex-1 px-3 py-6 space-y-2">
              <button
                onClick={() => { setActiveTab('patients'); setSidebarOpen(false) }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                  activeTab === 'patients'
                    ? 'bg-[#0066A4] text-white shadow-lg'
                    : 'text-gray-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                <span>Pacientes</span>
              </button>
              <button
                onClick={() => { setActiveTab('requests'); setSidebarOpen(false) }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                  activeTab === 'requests'
                    ? 'bg-[#0066A4] text-white shadow-lg'
                    : 'text-gray-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                <span>Solicitudes</span>
              </button>
              {currentUser?.role === 'admin' && (
                <button
                  onClick={() => { handleBackToAdmin(); setSidebarOpen(false) }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-gray-300 hover:bg-white/10 hover:text-white transition-all"
                >
                  <span>Volver a Admin</span>
                </button>
              )}
            </nav>

            {/* Info de usuario */}
            <div className="p-4 border-t border-white/10">
              <div className="mb-3">
                <p className="text-sm font-semibold truncate">{currentUser?.email}</p>
                <p className="text-xs text-gray-400 capitalize">{currentUser?.role === 'clinic' ? 'Clinica' : 'Administrador'}</p>
              </div>
              <button
                onClick={() => { handleLogout(); setSidebarOpen(false) }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded font-medium transition-all"
              >
                <span>Cerrar Sesion</span>
              </button>
            </div>
          </aside>

          {/* Overlay */}
          <div
            className="flex-1 bg-black/40"
            onClick={() => setSidebarOpen(false)}
          />
        </div>
      )}

      {/* Contenido principal */}
      <div className="flex-1 overflow-auto md:ml-64 pt-14 md:pt-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* PACIENTES */}
          {activeTab === 'patients' && (
            <div>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Pacientes</h1>
                <p className="text-sm text-gray-500 mt-1">{filteredPatients.length} paciente{filteredPatients.length !== 1 ? 's' : ''} registrado{filteredPatients.length !== 1 ? 's' : ''}</p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <input
                  type="text"
                  placeholder="Buscar paciente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 bg-white text-sm rounded focus:outline-none focus:border-[#0066A4] focus:ring-1 focus:ring-[#0066A4]"
                />
                {currentUser?.role === 'clinic' && (
                  <button
                    onClick={() => setShowNewPatientModal(true)}
                    className="px-4 py-2.5 bg-[#0066A4] text-white rounded text-sm font-medium hover:bg-[#003C63]"
                  >
                    Nuevo paciente
                  </button>
                )}
              </div>

              <div className="bg-white border border-gray-200 rounded shadow">
                {groupedPatients.length === 0 ? (
                  <div className="p-6 text-center text-gray-500 text-sm">No hay pacientes</div>
                ) : (
                  groupedPatients.map(([letter, items]) => (
                    <div key={letter}>
                      <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
                        <span className="text-sm font-bold text-gray-700">{letter}</span>
                      </div>
                      {items.map((patient) => (
                        <div
                          key={patient.id}
                          onClick={() => navigate(`/patient/${patient.id}`)}
                          className="px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors flex items-center justify-between"
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{patient.name}</p>
                            {patient.email && (
                              <p className="text-xs text-gray-500 truncate">{patient.email}</p>
                            )}
                          </div>
                          {currentUser?.role === 'admin' && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium mr-3">
                              {patient.clinic_name || 'Sin clinica'}
                            </span>
                          )}
                          <button className="px-3 py-1.5 bg-[#0066A4] text-white text-xs font-medium hover:bg-[#003C63] transition-colors">
                            Ver Perfil
                          </button>
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* SOLICITUDES */}
          {activeTab === 'requests' && (
            <div>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Solicitudes</h1>
                <p className="text-sm text-gray-500 mt-1">{activeRequests.length} solicitud{activeRequests.length !== 1 ? 'es' : ''} activa{activeRequests.length !== 1 ? 's' : ''}</p>
              </div>

              <div className="mb-6">
                <input
                  type="text"
                  placeholder="Buscar por nombre del paciente..."
                  value={requestSearchTerm}
                  onChange={(e) => setRequestSearchTerm(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 bg-white text-sm rounded focus:outline-none focus:border-[#0066A4] focus:ring-1 focus:ring-[#0066A4]"
                />
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                <button
                  onClick={() => setRequestStatusFilter('all')}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition ${
                    requestStatusFilter === 'all'
                      ? 'bg-[#0066A4] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Todas ({activeRequests.length})
                </button>
                <button
                  onClick={() => setRequestStatusFilter('pending')}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition ${
                    requestStatusFilter === 'pending'
                      ? 'bg-[#0066A4] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Pendientes ({activeRequests.filter((r) => r.status === 'pending').length})
                </button>
                <button
                  onClick={() => setRequestStatusFilter('in_production')}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition ${
                    requestStatusFilter === 'in_production'
                      ? 'bg-[#0066A4] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  En Produccion ({activeRequests.filter((r) => r.status === 'in_production').length})
                </button>
                <button
                  onClick={() => setRequestStatusFilter('ready')}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition ${
                    requestStatusFilter === 'ready'
                      ? 'bg-[#0066A4] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Listas ({activeRequests.filter((r) => r.status === 'ready').length})
                </button>
              </div>

              <div className="bg-white border border-gray-200 rounded shadow">
                {filteredRequests.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <p className="text-sm">No hay solicitudes</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredRequests.map((req) => {
                      const patient = patients.find((p) => p.id === req.patient_id)
                      const badge = statusBadges[req.status] || statusBadges.pending

                      return (
                        <div
                          key={req.id}
                          onClick={() => navigate(`/request/${req.id}`)}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-medium text-gray-900 text-sm">{patient?.name || 'Paciente desconocido'}</p>
                                <span className="text-xs text-gray-500">#{req.consecutive || req.id}</span>
                              </div>
                              <p className="text-xs text-gray-600">
                                {req.template_type || req.templateType || 'Solicitud'}
                                {req.doctor_name && ` - Dr. ${req.doctor_name}`}
                                {req.foot_side && ` - ${req.foot_side}`}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(req.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </p>
                            </div>

                            <span className={`px-2 py-1 text-xs font-medium rounded ${badge.bg} ${badge.text}`}>
                              {badge.label}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Modal Nuevo Paciente */}
      {showNewPatientModal && currentUser?.role === 'clinic' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h2 className="text-2xl font-semibold mb-6 text-gray-900">Registrar Nuevo Paciente</h2>
            <form className="space-y-5" onSubmit={handleCreatePatient}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre <span className="text-red-600">*</span></label>
                <input
                  name="firstName"
                  type="text"
                  placeholder="Ej: Juan"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primer Apellido <span className="text-red-600">*</span></label>
                <input
                  name="lastName1"
                  type="text"
                  placeholder="Ej: Perez"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Segundo Apellido <span className="text-red-600">*</span></label>
                <input
                  name="lastName2"
                  type="text"
                  placeholder="Ej: Garcia"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas Clinicas</label>
                <textarea
                  name="notes"
                  rows="3"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
                  placeholder="Opcional"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setShowNewPatientModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0066A4] text-white rounded-md text-sm font-medium hover:bg-[#003C63]"
                >
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
